import React, { useEffect, useState } from 'react';

import {
    Entity,
    EntityVendorCodes,
    GetEntitiesInput,
    GetEntitiesOutput,
    GetServiceLaunchDateInput,
    GetServiceLaunchDateOutput,
    ServiceLaunches
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';

import { uniqueId } from 'lodash';
import { useHistory, useParams } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { CM_UI_SUPPORT_SERVICE_ID_LIST } from 'src/constants/context';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { initialAddVendorState } from 'src/data/AddVendorData';
import { IToast } from 'src/types/IToaster';
import { IVendor, IVendorCode, IVendorCodeOverride, IVendorDetailsParams } from 'src/types/IVendor';
import { convertDateToYyyyMmDdFormat } from 'src/util/dateTimeUtil';
import { buildServiceLaunchDateMap } from 'src/util/metadataUtil';

import { ConfirmationModal } from '../ConfirmationModal';
import { AddNewVendor } from './AddNewVendor';
import { ConfirmNewVendor } from './ConfirmNewVendor';

export const AddVendor = () => {
    const [vendor, setVendor] = useState<IVendor>(initialAddVendorState);
    const [isConfirmNewVendorStep, setIsConfirmNewVendorStep] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [activeServiceLaunchDateMapping, setActiveServiceLaunchDateMapping] = useState<
        Record<number, ServiceLaunches>
    >({});

    const params: IVendorDetailsParams = useParams();
    const vendorId = parseInt(params.vendorId);
    const [isEditMode, setIsEditMode] = useState<boolean>(!!vendorId);

    const history = useHistory();

    const { setToasts, setTopAlertMsg } = React.useContext(AppContext);

    // Fetch service launch dates
    useEffect(() => {
        contractConfigurationClient.getServiceLaunchDate(GetServiceLaunchDateInput.clone({})).subscribe(
            (response: GetServiceLaunchDateOutput) =>
                setActiveServiceLaunchDateMapping(buildServiceLaunchDateMap(response.serviceLaunches ?? [])),
            (error: UnknownCoralError) => setTopAlertMsg(error.receivedException.message)
        );
    }, []);

    useEffect(() => {
        if (!vendorId) return;

        contractConfigurationClient
            .getEntitiesByIds(
                GetEntitiesInput.clone({
                    entityIds: [vendorId],
                    serviceIds: CM_UI_SUPPORT_SERVICE_ID_LIST,
                    includeIneligibleVendorCodes: true
                })
            )
            .subscribe(
                (response: GetEntitiesOutput) => {
                    if (response.entities && response.entities.length > 0) {
                        setIsEditMode(true);
                        const entity = response.entities[0];
                        const vendor = mapEntityToVendor(entity);
                        setVendor(vendor);
                    } else {
                        const toast: IToast = {
                            id: 'vendor-not-found-toast',
                            timeout: DEFAULT_TOASTER_TIMEOUT,
                            message: 'Vendor does not exist',
                            type: 'error'
                        };
                        setToasts([toast]);
                        history.push(ROUTES.VENDORS.INDEX);
                    }
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getEntitiesByIds unexpected error', error.receivedException.message);
                    const toast: IToast = {
                        id: 'vendor-not-found-toast',
                        timeout: DEFAULT_TOASTER_TIMEOUT,
                        message: error.receivedException.message,
                        type: 'error'
                    };
                    setToasts([toast]);
                    history.push(ROUTES.VENDORS.INDEX);
                }
            );
    }, []);

    const mapEntityToVendor = (entity: Entity) => {
        const activeServiceLaunchDateCount = Object.keys(activeServiceLaunchDateMapping)
            .filter((serviceId) => CM_UI_SUPPORT_SERVICE_ID_LIST.includes(parseInt(serviceId)))
            .reduce((accu, currServiceId) => {
                accu += activeServiceLaunchDateMapping[parseInt(currServiceId)].length;
                return accu;
            }, 0);

        const { defaultVendorCodes, overrideVendorCodes } = parseVendorCodes(
            entity.entityVendorCodes,
            activeServiceLaunchDateCount
        );

        return {
            vendorId: entity.entityId,
            vendorName: entity.entityName,
            description: entity.entityNotes,
            vendorCodes: defaultVendorCodes,
            vendorCodeOverrides: overrideVendorCodes,
            submitAccruals: entity.submitAccruals,
            submitPayments: entity.submitPayments
        } as IVendor;
    };

    const parseVendorCodes = (
        entityVendorCodes: EntityVendorCodes[] | undefined,
        activeServiceLaunchDateCount: number
    ) => {
        const defaultVendorCodes: IVendorCode[] = [];
        const overrideVendorCodes: IVendorCodeOverride[] = [];

        if (!entityVendorCodes) return { defaultVendorCodes, overrideVendorCodes };

        const vendorCodeMappingMap: Record<string, EntityVendorCodes[]> = {};

        for (const vendorCode of entityVendorCodes) {
            const vendorCodeMappingKey = `${vendorCode.catalogVendorCode}-${vendorCode.royaltyVendorCode?.trim()}${
                vendorCode.startDate ? '-' + vendorCode.startDate.getTime() : ''
            }${vendorCode.endDate ? '-' + vendorCode.endDate.getTime() : ''}`;
            if (!vendorCodeMappingMap[vendorCodeMappingKey]) {
                vendorCodeMappingMap[vendorCodeMappingKey] = [];
            }

            vendorCodeMappingMap[vendorCodeMappingKey].push(vendorCode);
        }

        for (const key in vendorCodeMappingMap) {
            if (vendorCodeMappingMap[key].length > activeServiceLaunchDateCount / 2) {
                const defaultVendorCode = vendorCodeMappingMap[key][0];
                defaultVendorCodes.push({
                    vendorCodeId: uniqueId(),
                    isNewVendorCode: true,
                    catalogCode: defaultVendorCode.catalogVendorCode,
                    royaltyCode: defaultVendorCode.royaltyVendorCode,
                    mappingStart: convertDateToYyyyMmDdFormat(defaultVendorCode.startDate, true),
                    mappingEnd: convertDateToYyyyMmDdFormat(defaultVendorCode.endDate, true)
                } as IVendorCode);
            } else {
                for (const overrideVendorCode of vendorCodeMappingMap[key]) {
                    overrideVendorCodes.push({
                        vendorCodeId: overrideVendorCode.entityVendorCodeId?.toString(),
                        catalogCode: overrideVendorCode.catalogVendorCode,
                        royaltyCode: overrideVendorCode.royaltyVendorCode,
                        serviceId: overrideVendorCode.service,
                        territoryId: overrideVendorCode.territory,
                        mappingStart: convertDateToYyyyMmDdFormat(overrideVendorCode.startDate, true),
                        mappingEnd: convertDateToYyyyMmDdFormat(overrideVendorCode.endDate, true)
                    } as IVendorCodeOverride);
                }
            }
        }

        return { defaultVendorCodes, overrideVendorCodes };
    };

    const handleCancel = () => {
        const url = ROUTES.VENDORS.INDEX + (isEditMode ? `/${vendorId}` : '');
        history.push(url);
    };

    const handleNext = () => {
        setIsConfirmNewVendorStep(true);
    };

    return (
        <Row alignmentHorizontal="center">
            <Column width="984px">
                {isConfirmNewVendorStep ? (
                    <ConfirmNewVendor
                        vendor={vendor}
                        isEditMode={isEditMode}
                        onPrevious={() => setIsConfirmNewVendorStep(false)}
                        openCancelModal={() => setIsCancelModalOpen(true)}
                    />
                ) : (
                    <AddNewVendor
                        vendor={vendor}
                        setVendor={setVendor}
                        isEditMode={isEditMode}
                        onNext={handleNext}
                        openCancelModal={() => setIsCancelModalOpen(true)}
                    />
                )}
            </Column>
            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                title={isEditMode ? 'Cancel edit vendor?' : 'Cancel add vendor?'}
                body="Are you sure you want to cancel? Any changes will be lost."
                primaryButtonText={isEditMode ? 'Cancel edit vendor' : 'Cancel add vendor'}
                primaryButtonOnClick={handleCancel}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsCancelModalOpen(false)}
            />
        </Row>
    );
};
